/**
 * Punkt 149: Bereich „Passwort“ in „Person bearbeiten“ (Mockup freigegeben 06.10.2026,
 * https://claude.ai/artifact/DGuv6Bg3Rrq5KEf1Cnu15k).
 * Fremde Person: Server setzt per Stack-Server-API (beendet alle Sitzungen der Person).
 * Eigenes Passwort: Stack-Client, damit dieses Gerät angemeldet bleibt; danach Hinweis-Mail.
 * Das Passwort wird nie geloggt, nie gespeichert und nach dem Setzen aus dem Zustand gelöscht.
 */
import { useState } from "react";
import { useUser } from "@stackframe/react";
import { apiMutate } from "../hooks/useApi";
import type { ApiError } from "../lib/api";
import { darfWeiter, staerke, staerkeHinweis, vorschlag } from "../lib/passwort";
import { Button, ErrorNote, Icon, Input, Modal } from "./components";

type PasswortSchritt = "ruhe" | "eingabe" | "bestaetigen" | "erfolg";
type PasswortFehler = { titel: string; text: string };

const FEHLER_SCHWACH: PasswortFehler = { titel: "Dieses Passwort ist zu leicht zu erraten.", text: "Wähle ein längeres Passwort, am besten drei Wörter hintereinander." };
const FEHLER_DIENST: PasswortFehler = {
  titel: "Das Passwort konnte nicht gesetzt werden.",
  text: "Der Anmeldedienst antwortet gerade nicht. Das alte Passwort gilt weiter. Bitte versuche es gleich noch einmal.",
};
const FEHLER_EINRICHTUNG: PasswortFehler = {
  titel: "Passwort setzen ist noch nicht eingerichtet.",
  text: "Die Verbindung zum Anmeldedienst fehlt noch. Das alte Passwort gilt weiter. Bitte melde dich beim MAESTRO-Support.",
};
const FEHLER_ALT: PasswortFehler = { titel: "Das aktuelle Passwort stimmt nicht.", text: "Prüfe das aktuelle Passwort und versuche es noch einmal." };

const LABEL = "mb-1 block font-body-sm text-body-sm font-medium text-on-surface dark:text-inverse-on-surface";
const HINWEIS = "font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim";

type StackSelbst = {
  hasPassword?: boolean;
  updatePassword?: (d: { oldPassword: string; newPassword: string }) => Promise<unknown>;
  setPassword?: (d: { password: string }) => Promise<unknown>;
};

function stackFehler(res: unknown): PasswortFehler {
  const code = String((res as { errorCode?: string } | null)?.errorCode ?? "");
  if (code.includes("MISMATCH")) return FEHLER_ALT;
  if (code === "PASSWORD_AUTHENTICATION_NOT_ENABLED") return FEHLER_EINRICHTUNG;
  if (/^PASSWORD_(TOO_|REQUIREMENTS)/.test(code)) return FEHLER_SCHWACH;
  return FEHLER_DIENST;
}

export function PasswortBereich({
  stackUserId,
  name,
  selbst,
  darf,
}: {
  stackUserId: string;
  name: string;
  selbst: boolean;
  darf: boolean;
}) {
  const user = useUser() as unknown as StackSelbst | null;
  const altNoetig = selbst && !!user?.hasPassword;
  const [zustand, setZustand] = useState<PasswortSchritt>("ruhe");
  const [alt, setAlt] = useState("");
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [sichtbar, setSichtbar] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<PasswortFehler | null>(null);

  function leeren() {
    setAlt("");
    setPw1("");
    setPw2("");
    setSichtbar(false);
  }
  function abbrechen() {
    leeren();
    setFehler(null);
    setZustand("ruhe");
  }

  async function setzen() {
    setBusy(true);
    setFehler(null);
    try {
      if (selbst) {
        const res = altNoetig
          ? await user?.updatePassword?.({ oldPassword: alt, newPassword: pw1 })
          : await user?.setPassword?.({ password: pw1 });
        if (res) throw Object.assign(new Error("stack"), { stack_: res });
        // Hinweis-Mail und Protokoll; scheitert das, ist das Passwort trotzdem gesetzt.
        await apiMutate(`/api/members/${stackUserId}/passwort-hinweis`, "POST", {}).catch(() => undefined);
      } else {
        await apiMutate(`/api/members/${stackUserId}/passwort`, "POST", { passwort: pw1 });
      }
      leeren();
      setZustand("erfolg");
    } catch (e) {
      const s = (e as { stack_?: unknown }).stack_;
      const code = (e as ApiError).code;
      setFehler(
        s !== undefined ? stackFehler(s)
        : code === "schwaches_passwort" ? FEHLER_SCHWACH
        : code === "nicht_eingerichtet" ? FEHLER_EINRICHTUNG
        : FEHLER_DIENST,
      );
      setZustand("eingabe");
    } finally {
      setBusy(false);
    }
  }

  const st = staerke(pw1);
  const ok = darfWeiter(pw1, pw2) && (!altNoetig || alt.length > 0);

  return (
    <div className="space-y-3 border-t border-outline-variant/30 pt-4 dark:border-outline/30" data-testid="passwort-bereich"
      // Enter in einem Passwortfeld darf nicht das umgebende Profil-Formular abschicken.
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target instanceof HTMLInputElement) e.preventDefault();
      }}
    >
      <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Passwort</span>
      {!darf ? (
        <p className={`flex items-center gap-2 ${HINWEIS}`}>
          <Icon name="lock" className="text-[18px]" />
          Passwörter anderer Personen setzen nur Inhaber. Dein eigenes kannst du selbst ändern.
        </p>
      ) : zustand === "ruhe" ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className={HINWEIS}>
            {selbst ? "Du meldest dich mit E-Mail und Passwort an." : `${name} meldet sich mit E-Mail und Passwort an.`} Das Passwort selbst wird nie angezeigt.
          </p>
          <Button type="button" variant="secondary" className="inline-flex items-center gap-1 !py-1.5" onClick={() => setZustand("eingabe")}>
            <Icon name="key" className="text-[18px]" />
            Passwort setzen
          </Button>
        </div>
      ) : zustand === "erfolg" ? (
        <div className="space-y-2" role="status">
          <p className="flex items-center gap-2 font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
            <Icon name="check_circle" className="text-[18px] text-tertiary dark:text-tertiary-fixed-dim" />
            Passwort gesetzt.
          </p>
          <p className={HINWEIS}>
            {selbst
              ? "Du bleibst auf diesem Gerät angemeldet, alle anderen Geräte wurden abgemeldet."
              : `${name} ist auf allen Geräten abgemeldet und meldet sich mit dem neuen Passwort an. ${name} bekommt eine Mail „Dein Passwort wurde geändert“ (ohne Passwort), alle Inhaber einen Hinweis. Teile das Passwort persönlich mit.`}
          </p>
          <Button type="button" variant="secondary" className="!py-1.5" onClick={() => setZustand("ruhe")}>
            Fertig
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {fehler && <ErrorNote title={fehler.titel} message={fehler.text} />}
          {altNoetig && (
            <label className="block">
              <span className={LABEL}>Aktuelles Passwort</span>
              <Input type="password" autoComplete="current-password" value={alt} onChange={(e) => setAlt(e.target.value)} />
            </label>
          )}
          <label className="block">
            <span className={LABEL}>Neues Passwort</span>
            <span className="flex gap-2">
              <Input
                type={sichtbar ? "text" : "password"}
                autoComplete="new-password"
                aria-describedby="passwort-hinweis"
                value={pw1}
                maxLength={200}
                onChange={(e) => setPw1(e.target.value)}
              />
              <Button type="button" variant="secondary" className="!px-2 !py-1.5" aria-label={sichtbar ? "Passwort verbergen" : "Passwort anzeigen"} onClick={() => setSichtbar(!sichtbar)}>
                <Icon name={sichtbar ? "visibility_off" : "visibility"} className="text-[18px]" />
              </Button>
            </span>
            <span className="mt-2 flex gap-1" aria-hidden="true" data-testid="passwort-staerke" data-staerke={st}>
              {[1, 2, 3, 4].map((i) => (
                <i key={i} className={`h-1 flex-1 rounded-full ${i <= st ? (st >= 3 ? "bg-primary" : "bg-error") : "bg-outline-variant/40"}`} />
              ))}
            </span>
            <span id="passwort-hinweis" className={`mt-1 block ${HINWEIS}`}>{staerkeHinweis(pw1)}</span>
          </label>
          <label className="block">
            <span className={LABEL}>Neues Passwort wiederholen</span>
            <Input type={sichtbar ? "text" : "password"} autoComplete="new-password" value={pw2} maxLength={200} onChange={(e) => setPw2(e.target.value)} />
          </label>
          {pw2.length > 0 && pw1 !== pw2 && <p className={HINWEIS}>Die beiden Passwörter stimmen nicht überein.</p>}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              className="inline-flex items-center gap-1"
              onClick={() => {
                const v = vorschlag();
                setPw1(v);
                setPw2(v);
                setSichtbar(true);
              }}
            >
              <Icon name="auto_awesome" className="text-[18px]" />
              Vorschlag erzeugen
            </Button>
            <span className="flex gap-2">
              <Button type="button" variant="secondary" onClick={abbrechen}>
                Abbrechen
              </Button>
              <Button type="button" disabled={!ok} onClick={() => setZustand("bestaetigen")}>
                Weiter
              </Button>
            </span>
          </div>
        </div>
      )}
      <Modal
        open={zustand === "bestaetigen"}
        onClose={() => setZustand("eingabe")}
        width="480px"
        title={`Passwort für ${selbst ? "dich" : name} setzen?`}
        subtitle={`${selbst ? "Dein" : "Das"} bisherige Passwort gilt danach nicht mehr.`}
        testId="passwort-bestaetigen"
        footer={
          <>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setZustand("eingabe")}>
              Zurück
            </Button>
            <Button type="button" disabled={busy} className="inline-flex items-center gap-1" onClick={() => void setzen()}>
              <Icon name="key" className="text-[18px]" />
              Passwort setzen
            </Button>
          </>
        }
      >
        <ul className="list-disc space-y-1 pl-5 font-body-md text-body-md text-on-surface dark:text-inverse-on-surface">
          <li>{selbst ? "Alle anderen Geräte werden abgemeldet." : `${name} wird auf allen Geräten abgemeldet.`}</li>
          <li>
            {selbst
              ? "Du bleibst hier angemeldet. Du und alle Inhaber bekommen eine Hinweis-Mail."
              : `${name} bekommt eine Mail „Dein Passwort wurde geändert“ (ohne Passwort), alle Inhaber einen Hinweis. Teile das Passwort persönlich mit.`}
          </li>
          <li>Der Vorgang wird im Protokoll vermerkt (ohne das Passwort).</li>
        </ul>
      </Modal>
    </div>
  );
}
